import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTheme, type ThemeMode } from "@/contexts/ThemeContext";
import { Sun, Moon, Monitor, BookOpen, Plus, Trash2, Loader2, Lock, Search, Bell } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { isWorkingDay } from "@shared/workweek";
import { cn } from "@/lib/utils";

const TIMEZONES = [
  "UTC",
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Qatar",
  "Asia/Kuwait",
  "Africa/Cairo",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
];

/** Deep-linkable sections — the More hub opens the dialog scrolled to one
 *  of these (UX: tapping "Notifications" must LAND on Notifications, not at
 *  the top of a long dialog). */
export type SettingsSection = "workweek" | "privacy" | "notifications";

/**
 * Whether to show the "Privacy & sharing" settings (default sharing level +
 * blocker visibility). These control what TEAMMATES see, so they only matter
 * if the user belongs to ≥1 team workspace. Keyed on having ANY team
 * workspace (a user-global property), NOT on the active tab — so a team
 * member viewing their Personal workspace keeps the controls. Shown while
 * the workspace list is still loading to avoid hiding them from team members.
 */
export function showTeamSharingSettings(ctx: {
  isLoading: boolean;
  workspaces: ReadonlyArray<{ team: { isPersonal: boolean } }>;
}): boolean {
  return ctx.isLoading || ctx.workspaces.some(w => !w.team.isPersonal);
}

export function SettingsDialog({
  open,
  onOpenChange,
  initialSection,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** When set, the dialog scrolls this section into view on open. */
  initialSection?: SettingsSection;
}) {
  const { t, locale, setLocale } = useI18n();
  const utils = trpc.useUtils();
  // "Privacy & sharing" (default sharing level + blocker visibility) only
  // matters if the user belongs to at least one TEAM workspace — those are
  // controls for what teammates see. A user with ONLY a personal workspace
  // has no team audience, so the section is hidden. NB: this keys on whether
  // the user has ANY team workspace (a user-global property, since the
  // preference applies across all their teams) — NOT on the active tab, so a
  // CCED member viewing their Personal workspace still sees the controls.
  const wsCtx = useActiveWorkspace();
  const showTeamSharing = showTeamSharingSettings(wsCtx);
  const meQuery = trpc.preferences.me.useQuery(undefined, { enabled: open });
  const updateMutation = trpc.preferences.update.useMutation({
    onSuccess: () => utils.preferences.me.invalidate(),
  });
  const demoStatusQuery = trpc.seed.status.useQuery(undefined, { enabled: open });
  const clearDemoMutation = trpc.seed.clearDemo.useMutation({
    onSuccess: () => {
      utils.seed.status.invalidate();
      utils.signals.forWeek.invalidate();
      utils.receipts.forWeek.invalidate();
      utils.team.mine.invalidate();
      toast.success(t.demoCleared);
    },
  });

  const [workingDays, setWorkingDays] = useState(31);
  const [weekStartDay, setWeekStartDay] = useState(0);
  const [tz, setTz] = useState("UTC");
  const [hijri, setHijri] = useState(false);
  const [ramadan, setRamadan] = useState(false);
  const [voiceLang, setVoiceLang] = useState<
    "auto" | "bilingual" | "en" | "ar"
  >("auto");

  useEffect(() => {
    if (!meQuery.data) return;
    setWorkingDays(meQuery.data.workingDays);
    setWeekStartDay(meQuery.data.weekStartDay);
    setTz(meQuery.data.timezone);
    setHijri(meQuery.data.hijriEnabled);
    setRamadan(meQuery.data.ramadanAware);
    const mode = (meQuery.data as any).voiceLanguageMode;
    if (mode === "auto" || mode === "bilingual" || mode === "en" || mode === "ar") {
      setVoiceLang(mode);
    }
  }, [meQuery.data]);

  // EVERYTHING in this dialog applies INSTANTLY. It used to be split-brain:
  // privacy/notifications/theme/language/vocabulary saved on change while
  // the work-week fields waited for a Save button — so "Cancel" silently
  // kept most changes (user-reported). One model now: change → saved.
  const persist = (partial: Record<string, unknown>) =>
    updateMutation.mutate(partial as any);

  const toggleDay = (d: number) => {
    setWorkingDays(prev => {
      const next = prev ^ (1 << d);
      persist({ workingDays: next });
      return next;
    });
  };

  // Scroll the requested section into view once the dialog content has
  // mounted. The base DialogContent is the scroll container
  // (max-h + overflow-y-auto), so scrollIntoView scrolls INSIDE the modal —
  // works on mobile Safari and desktop alike. A short timeout lets Radix
  // finish mounting/animating before we measure; instant (non-smooth)
  // scrolling avoids racing the open animation.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const container = contentRef.current;
      if (!container) return;
      // "workweek" IS the top region of the dialog — anchor-scrolling to it
      // on mobile pushed the header/title off-screen and the dialog looked
      // half-opened. Top-of-dialog destinations (and plain opens) start
      // FULLY scrolled up; only the deeper sections jump.
      if (!initialSection || initialSection === "workweek") {
        container.scrollTop = 0;
        return;
      }
      const el = container.querySelector<HTMLElement>(
        `[data-settings-section="${initialSection}"]`,
      );
      el?.scrollIntoView({ block: "start" });
    }, 80);
    return () => window.clearTimeout(id);
  }, [open, initialSection]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={contentRef} className="sm:max-w-lg overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{t.settings}</DialogTitle>
          <DialogDescription>
            {locale === "ar"
              ? "تُحفَظ التغييرات تلقائياً فور إجرائها."
              : "Changes are saved automatically as you make them."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t.locale}
            </Label>
            <div className="mt-2 inline-flex rounded-lg bg-muted p-1">
              <button
                onClick={() => { setLocale("en"); persist({ locale: "en" }); }}
                className={cn(
                  "px-4 py-1.5 text-sm rounded-md transition-colors",
                  locale === "en" ? "bg-background shadow-sm" : "text-muted-foreground",
                )}
              >
                English
              </button>
              <button
                onClick={() => { setLocale("ar"); persist({ locale: "ar" }); }}
                className={cn(
                  "px-4 py-1.5 text-sm rounded-md transition-colors",
                  locale === "ar" ? "bg-background shadow-sm" : "text-muted-foreground",
                )}
              >
                العربية
              </button>
            </div>
          </div>

          <div data-settings-section="workweek" className="scroll-mt-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t.workingDays}
            </Label>
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {t.days.map((dn, i) => {
                const on = isWorkingDay(workingDays, i);
                return (
                  <button
                    key={i}
                    onClick={() => toggleDay(i)}
                    className={cn(
                      "h-9 min-w-[44px] rounded-lg text-sm font-medium border transition-all",
                      on
                        ? "bg-coral text-white border-coral"
                        : "bg-card text-foreground border-border hover:bg-muted",
                    )}
                  >
                    {dn}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t.weekStart}
            </Label>
            <Select value={String(weekStartDay)} onValueChange={v => { setWeekStartDay(Number(v)); persist({ weekStartDay: Number(v) }); }}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {t.daysFull.map((dn, i) => (
                  <SelectItem key={i} value={String(i)}>{dn}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t.timezone}
            </Label>
            <Select value={tz} onValueChange={v => { setTz(v); persist({ timezone: v }); }}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(z => (
                  <SelectItem key={z} value={z}>{z}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="hijri" className="cursor-pointer">{t.hijri}</Label>
            <Switch id="hijri" checked={hijri} onCheckedChange={v => { setHijri(v); persist({ hijriEnabled: v }); }} />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="ram" className="cursor-pointer">{t.ramadan}</Label>
            <Switch id="ram" checked={ramadan} onCheckedChange={v => { setRamadan(v); persist({ ramadanAware: v }); }} />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t.voiceLanguage}
            </Label>
            <div className="text-xs text-muted-foreground mt-1 mb-2">
              {t.voiceLanguageHelp}
            </div>
            <Select value={voiceLang} onValueChange={v => { setVoiceLang(v as any); persist({ voiceLanguageMode: v }); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">{t.voiceLangAuto}</SelectItem>
                <SelectItem value="bilingual">{t.voiceLangBilingual}</SelectItem>
                <SelectItem value="en">{t.voiceLangEnglish}</SelectItem>
                <SelectItem value="ar">{t.voiceLangArabic}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <ThemeChooser />


          {demoStatusQuery.data?.demoActive && (
            <div className="flex items-start justify-between gap-3 pt-3 border-t border-border/60">
              <div>
                <div className="text-sm font-medium">{t.demoActiveLabel}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t.demoActiveHelp}</div>
              </div>
              <button
                onClick={() => clearDemoMutation.mutate()}
                disabled={clearDemoMutation.isPending}
                className="text-sm text-coral hover:underline disabled:opacity-50 whitespace-nowrap"
              >
                {clearDemoMutation.isPending ? "…" : t.demoActiveLabel}
              </button>
            </div>
          )}

          <VocabularyEditor />

          {/* scroll-mt-12 ≈ the sticky close button's height + breathing room,
              so a deep-linked section lands BELOW the X instead of under it.
              Hidden when the user has no team workspace — sharing-level and
              blocker-visibility controls only mean something with a team. */}
          {showTeamSharing ? (
            <div data-settings-section="privacy" className="scroll-mt-12">
              <PrivacyPreferences />
            </div>
          ) : null}

          <div data-settings-section="notifications" className="scroll-mt-12">
            <NotificationPreferences />
          </div>

          <SignOutEverywhere />
          <DeleteAccount />
        </div>

        {/* Instant-apply model: no Save/Cancel pair (it lied — most sections
            already applied on change). One honest "Done". */}
        <DialogFooter className="sm:justify-between">
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors self-center"
          >
            {t.privacyPolicy}
          </a>
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-coral hover:bg-coral/90 text-white"
            data-testid="settings-done"
          >
            {locale === "ar" ? "تم" : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Theme picker — light / dark / follow-system. Persists to localStorage
 * via ThemeProvider; system mode subscribes to prefers-color-scheme so
 * the UI flips live if the user changes OS settings.
 */
function ThemeChooser() {
  const { mode, setMode } = useTheme();
  const { t } = useI18n();
  const opts: Array<{ id: ThemeMode; label: string; Icon: React.ComponentType<{ className?: string }> }> = [
    { id: "light", label: t.themeLight, Icon: Sun },
    { id: "dark", label: t.themeDark, Icon: Moon },
    { id: "system", label: t.themeSystem, Icon: Monitor },
  ];
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {t.appearance}
      </Label>
      <div className="mt-2 inline-flex rounded-lg bg-muted p-1">
        {opts.map(o => (
          <button
            key={o.id}
            onClick={() => setMode(o.id)}
            className={cn(
              "px-3 py-1.5 text-sm rounded-md transition-colors inline-flex items-center gap-1.5",
              mode === o.id ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            <o.Icon className="h-3.5 w-3.5" />
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Bumps the user's tokenVersion server-side, invalidating every outstanding
 * session JWT for this account (including the current one) and redirecting
 * to the login screen. Standard B2B "Sign out everywhere" surface.
 */
function SignOutEverywhere() {
  const { t, locale } = useI18n();
  const logoutEverywhere = trpc.auth.logoutEverywhere.useMutation({
    onSuccess: () => {
      // Cookie is already cleared server-side; bounce to root so the auth
      // guard kicks in and routes the user to the login flow.
      window.location.href = "/";
    },
    onError: e => toast.error(e.message),
  });
  return (
    <div className="flex items-start justify-between gap-3 pt-3 border-t border-border/60">
      <div>
        <div className="text-sm font-medium">{t.signOutEverywhere}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {t.signOutEverywhereHelp}
        </div>
      </div>
      <button
        onClick={() => {
          if (
            window.confirm(
              locale === "ar"
                ? "تسجيل الخروج من جميع الأجهزة؟"
                : "Sign out of every device?",
            )
          ) {
            logoutEverywhere.mutate();
          }
        }}
        disabled={logoutEverywhere.isPending}
        className="text-sm text-red-600 hover:underline disabled:opacity-50 whitespace-nowrap"
      >
        {logoutEverywhere.isPending ? "…" : t.signOutEverywhere}
      </button>
    </div>
  );
}

/**
 * Permanent account deletion. Required by both Apple App Store §5.1.1(v)
 * and Google Play "User Data" policies — without an in-app delete path,
 * store reviewers will reject the submission. The user must type DELETE
 * in the confirmation prompt to proceed; this matches Apple's expectation
 * that account deletion be "unambiguous" and not triggered by a stray tap.
 */
function DeleteAccount() {
  const { t, locale } = useI18n();
  const deleteAccount = trpc.auth.deleteAccount.useMutation({
    onSuccess: () => {
      window.location.href = "/";
    },
    onError: e => toast.error(e.message),
  });
  return (
    <div className="flex items-start justify-between gap-3 pt-3 border-t border-border/60">
      <div>
        <div className="text-sm font-medium text-red-600">{t.deleteAccount}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {t.deleteAccountHelp}
        </div>
      </div>
      <button
        onClick={() => {
          const expected = locale === "ar" ? "حذف" : "DELETE";
          const ans = window.prompt(
            locale === "ar"
              ? `لتأكيد الحذف الدائم لحسابك وكل بياناتك، اكتب: ${expected}`
              : `To permanently delete your account and all data, type: ${expected}`,
          );
          if (ans?.trim() === expected) {
            deleteAccount.mutate({ confirm: "DELETE" });
          }
        }}
        disabled={deleteAccount.isPending}
        className="text-sm text-red-600 hover:underline disabled:opacity-50 whitespace-nowrap"
      >
        {deleteAccount.isPending ? "…" : t.deleteAccount}
      </button>
    </div>
  );
}

/**
 * Domain-vocabulary editor — owner-only. Each row in this list is
 * injected into every LLM prompt (daily/weekly draft, voice
 * classifier, team Q&A) as a "preserve verbatim if present in input"
 * directive. Use it for internal product names, customer codenames,
 * Arabic-only phrases — anything the model would otherwise paraphrase
 * into something generic.
 *
 * Read-only when the caller isn't the team owner; the section still
 * renders so members can see what their owner has configured (helps
 * with "why is the model mis-spelling X?" debugging).
 */
function VocabularyEditor() {
  const { locale } = useI18n();
  const [draftTerm, setDraftTerm] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [draftPron, setDraftPron] = useState("");
  // Client-side filter — `vocabulary.list` returns the whole team
  // glossary in one shot (cap is 60 terms in the LLM prompt but
  // the table can hold hundreds). With 450+ imported MOM terms,
  // scanning the list visually is painful, so we filter client-side
  // by term + note substring (case-insensitive).
  const [search, setSearch] = useState("");

  const teamsQ = trpc.team.mine.useQuery();
  const teamId = teamsQ.data?.[0]?.team.id ?? null;
  // Editing is open to every team member — vocabulary is collaborative
  // team knowledge, and the audit log captures provenance per term.

  const utils = trpc.useUtils();
  const listQ = trpc.vocabulary.list.useQuery(
    { teamId: teamId ?? -1 },
    { enabled: !!teamId },
  );
  const addM = trpc.vocabulary.add.useMutation();
  const removeM = trpc.vocabulary.remove.useMutation();

  async function add() {
    if (!teamId) return;
    const term = draftTerm.trim();
    if (!term) return;
    try {
      await addM.mutateAsync({
        teamId,
        term,
        note: draftNote.trim() || undefined,
        pronunciation: draftPron.trim() || undefined,
      });
      setDraftTerm("");
      setDraftNote("");
      setDraftPron("");
      utils.vocabulary.list.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't add term");
    }
  }

  async function remove(id: number) {
    if (!teamId) return;
    try {
      await removeM.mutateAsync({ teamId, id });
      utils.vocabulary.list.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't remove");
    }
  }

  const allItems = listQ.data ?? [];
  const q = search.trim().toLowerCase();
  const items = q
    ? allItems.filter(
        t =>
          t.term.toLowerCase().includes(q) ||
          (t.note ?? "").toLowerCase().includes(q) ||
          ((t as any).pronunciation ?? "").toLowerCase().includes(q),
      )
    : allItems;

  return (
    <div className="pt-3 border-t border-border/60">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg bg-coral-soft p-2 shrink-0">
          <BookOpen className="h-4 w-4 text-coral" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {locale === "ar" ? "مصطلحات المجال" : "Domain vocabulary"}
            {allItems.length > 0 && (
              <span className="ms-2 text-xs font-normal text-muted-foreground">
                ({allItems.length})
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {locale === "ar"
              ? "مشترك مع كل أعضاء الفريق. أي عضو يستطيع إضافة وتعديل وحذف المصطلحات."
              : "Shared with the whole team. Any member can add, edit, or remove terms."}
          </div>
        </div>
      </div>

      {/* Add row. Three inputs (term, pronunciation hint, note) +
          Add button. Stacks vertically on phones so each gets full
          width; sm: collapses to a single inline row. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
        <input
          type="text"
          value={draftTerm}
          onChange={e => setDraftTerm(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && draftTerm.trim()) {
              e.preventDefault();
              add();
            }
          }}
          placeholder={
            locale === "ar"
              ? "مصطلح، مثل: وصل"
              : "Term, e.g. Wassel"
          }
          disabled={addM.isPending}
          className="w-full sm:flex-1 sm:w-auto min-w-0 h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <input
          type="text"
          value={draftPron}
          onChange={e => setDraftPron(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && draftTerm.trim()) {
              e.preventDefault();
              add();
            }
          }}
          placeholder={
            locale === "ar"
              ? "نطق (اختياري)، مثل: وَصْل"
              : "Sounds like (e.g. wah-sel)"
          }
          disabled={addM.isPending}
          className="w-full sm:w-32 sm:flex-shrink min-w-0 h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <input
          type="text"
          value={draftNote}
          onChange={e => setDraftNote(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && draftTerm.trim()) {
              e.preventDefault();
              add();
            }
          }}
          placeholder={
            locale === "ar" ? "ملاحظة (اختياري)" : "Note (optional)"
          }
          disabled={addM.isPending}
          className="w-full sm:flex-1 sm:w-auto min-w-0 h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <Button
          onClick={add}
          disabled={addM.isPending || !draftTerm.trim()}
          size="sm"
          className="bg-coral hover:bg-coral/90 text-white shrink-0 gap-1 self-end sm:self-auto"
        >
          {addM.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          {locale === "ar" ? "أضف" : "Add"}
        </Button>
      </div>

      {/* Search filter. Shown only when there's enough content to
          warrant scanning — for a 5-term glossary the search box is
          noise; at 50+ terms it's essential. */}
      {allItems.length >= 5 && (
        <div className="relative mb-2">
          <Search className="absolute start-2 top-2 h-3.5 w-3.5 text-muted-foreground/70 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={
              locale === "ar"
                ? `ابحث في ${allItems.length} مصطلح…`
                : `Search ${allItems.length} terms…`
            }
            className="w-full h-8 rounded-md border border-input bg-background ps-7 pe-7 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label={locale === "ar" ? "مسح" : "Clear"}
              className="absolute end-2 top-2 text-muted-foreground/70 hover:text-foreground"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
        {listQ.isLoading && (
          <div className="text-xs text-muted-foreground py-2 text-center">
            <Loader2 className="h-3.5 w-3.5 animate-spin inline-block me-1" />
            {locale === "ar" ? "جارٍ التحميل…" : "Loading…"}
          </div>
        )}
        {!listQ.isLoading && allItems.length === 0 && (
          <div className="text-xs text-muted-foreground italic py-2 text-center">
            {locale === "ar"
              ? "لا توجد مصطلحات بعد. أضف أول مصطلح أعلاه."
              : "No terms yet. Add your first one above."}
          </div>
        )}
        {!listQ.isLoading &&
          allItems.length > 0 &&
          items.length === 0 && (
            <div className="text-xs text-muted-foreground italic py-2 text-center">
              {locale === "ar"
                ? `لا توجد نتائج لـ "${search}"`
                : `No matches for "${search}"`}
            </div>
          )}
        {items.map(t => (
          <div
            key={t.id}
            className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/20 px-2 py-1.5 text-sm"
          >
            <div className="flex-1 min-w-0 break-words">
              {/* `break-all` on the term so an unbreakable string like
                  "AsiaPacific-Customer-Engagement-Platform" wraps
                  rather than forcing horizontal scroll on the whole
                  dialog. Notes break by words. */}
              <span className="font-mono break-all">{t.term}</span>
              {(t as any).pronunciation && (
                <span className="text-xs text-coral/80 ms-2 italic break-words">
                  · {locale === "ar" ? "يُنطق" : "sounds like"}: {(t as any).pronunciation}
                </span>
              )}
              {t.note && (
                <span className="text-xs text-muted-foreground ms-2 break-words">
                  · {t.note}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(t.id)}
              disabled={removeM.isPending}
              className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 shrink-0"
              title={locale === "ar" ? "حذف" : "Remove"}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Privacy preferences — controls what teammates see about you.
 *
 * Two knobs:
 *   - shareReceiptDetail: how much of your APPROVED receipts peers
 *     can read (headline only / full body / manager-only).
 *   - shareBlockerText: whether peers see actual blocker text or
 *     just a "Blocked" red dot.
 *
 * Drafts are NEVER peer-visible regardless of these settings —
 * that's a structural rule enforced server-side in team.handoff /
 * team.weekDigest / team.ask. The first-run disclosure modal
 * surfaces this contract before peers ever see anything.
 */
function PrivacyPreferences() {
  const { locale } = useI18n();
  const prefsQ = trpc.preferences.me.useQuery();
  const utils = trpc.useUtils();
  const update = trpc.preferences.update.useMutation({
    onSuccess: () => {
      utils.preferences.me.invalidate();
      toast.success(locale === "ar" ? "تم التحديث" : "Updated");
    },
    onError: e => toast.error(e.message),
  });

  const detail = prefsQ.data?.shareReceiptDetail ?? "headline";
  const blocker = prefsQ.data?.shareBlockerText ?? "manager-only";

  return (
    <div className="pt-3 border-t border-border/60">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg bg-coral-soft p-2 shrink-0">
          <Lock className="h-4 w-4 text-coral" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {locale === "ar" ? "الخصوصية والمشاركة" : "Privacy & sharing"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {locale === "ar"
              ? "تنطبق هذه الإعدادات على مساحات عمل الفِرق. مساحتك الشخصية خاصّة دائماً ولا يُشارَك منها شيء. تتحكّم بما يراه زملاؤك؛ ولا تظهر المسوّدات قبل الاعتماد."
              : "These apply to your team workspaces. Your personal workspace is always private. Nothing is shared from it. They control what teammates see; drafts are never visible to peers, only approved receipts."}
          </div>
        </div>
      </div>

      <div className="space-y-3 ms-11">
        <div>
          <Label className="text-xs mb-1.5 block">
            {locale === "ar"
              ? "مستوى المشاركة الافتراضي"
              : "Default sharing level"}
          </Label>
          <Select
            value={detail}
            onValueChange={v =>
              update.mutate({ shareReceiptDetail: v as any })
            }
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="headline">
                {locale === "ar" ? "العنوان فقط" : "Headline only"}
              </SelectItem>
              <SelectItem value="highlights">
                {locale === "ar"
                  ? "العنوان + النقاط (موصى به)"
                  : "Headline + highlights (recommended)"}
              </SelectItem>
              <SelectItem value="full">
                {locale === "ar" ? "الإيصال الكامل" : "Full receipt"}
              </SelectItem>
              <SelectItem value="manager-only">
                {locale === "ar" ? "المدير فقط" : "Manager only"}
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {locale === "ar"
              ? "يمكنك تغيير المستوى لكل إيصال عند الاعتماد."
              : "You can override the level per receipt at approval."}
          </p>
        </div>

        <div>
          <Label className="text-xs mb-1.5 block">
            {locale === "ar" ? "نص العوائق" : "Blocker text"}
          </Label>
          <Select
            value={blocker}
            onValueChange={v =>
              update.mutate({ shareBlockerText: v as any })
            }
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manager-only">
                {locale === "ar"
                  ? "المدير فقط (الافتراضي)"
                  : "Manager only (default)"}
              </SelectItem>
              <SelectItem value="team">
                {locale === "ar" ? "كل الفريق" : "Everyone on team"}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

/**
 * Configurable notifications (batch item 4). Per-channel master
 * toggles (in-app / email / mobile push) + per-event on/off.
 * Browser/web push is reserved (disabled with a note) until the
 * service-worker + VAPID work lands. Saves the full normalized
 * object each change (no partial-merge bugs).
 */
type NPrefs = {
  channels: { inApp: boolean; email: boolean; push: boolean };
  events: { insight: boolean; blocker: boolean; priority: boolean; weekly: boolean };
};
const NPREF_DEFAULT: NPrefs = {
  channels: { inApp: true, email: false, push: false },
  events: { insight: true, blocker: true, priority: true, weekly: true },
};
function normNPrefs(raw: unknown): NPrefs {
  const d = NPREF_DEFAULT;
  const r = (raw && typeof raw === "object" ? raw : {}) as any;
  const ch = r.channels && typeof r.channels === "object" ? r.channels : {};
  const ev = r.events && typeof r.events === "object" ? r.events : {};
  const b = (v: unknown, fb: boolean) => (typeof v === "boolean" ? v : fb);
  return {
    channels: {
      inApp: b(ch.inApp, d.channels.inApp),
      email: b(ch.email, d.channels.email),
      push: b(ch.push, d.channels.push),
    },
    events: {
      insight: b(ev.insight, d.events.insight),
      blocker: b(ev.blocker, d.events.blocker),
      priority: b(ev.priority, d.events.priority),
      weekly: b(ev.weekly, d.events.weekly),
    },
  };
}

function NotificationPreferences() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const prefsQ = trpc.preferences.me.useQuery();
  const utils = trpc.useUtils();
  const update = trpc.preferences.update.useMutation({
    onSuccess: () => {
      utils.preferences.me.invalidate();
      toast.success(isAr ? "تم التحديث" : "Updated");
    },
    onError: e => toast.error(e.message),
  });
  const p = normNPrefs((prefsQ.data as any)?.notificationPrefs);

  const save = (next: NPrefs) =>
    update.mutate({ notificationPrefs: next } as any);

  const ChannelRow = ({
    label,
    desc,
    on,
    onChange,
    disabled,
  }: {
    label: string;
    desc?: string;
    on: boolean;
    onChange?: (v: boolean) => void;
    disabled?: boolean;
  }) => (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm">{label}</div>
        {desc && (
          <div className="text-[11px] text-muted-foreground">{desc}</div>
        )}
      </div>
      <Switch
        checked={on}
        disabled={disabled || update.isPending}
        onCheckedChange={v => onChange?.(v)}
      />
    </div>
  );

  const evLabel: Record<keyof NPrefs["events"], string> = {
    insight: isAr ? "تنبيهات رئيس الأركان" : "Chief-of-Staff insights",
    blocker: isAr ? "تنبيهات العوائق" : "Aging-blocker alerts",
    priority: isAr ? "أولوية أُسندت لك" : "Priority assigned to you",
    weekly: isAr ? "الإيصال الأسبوعي" : "Weekly receipt",
  };

  return (
    <div className="pt-3 border-t border-border/60">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg bg-coral-soft p-2 shrink-0">
          <Bell className="h-4 w-4 text-coral" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {isAr ? "الإشعارات" : "Notifications"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {isAr
              ? "اختر القنوات والأحداث. الافتراضي: داخل التطبيق فقط."
              : "Choose channels + which events notify you. Default: in-app only."}
          </div>
        </div>
      </div>

      <div className="space-y-4 ms-11">
        <div className="space-y-2">
          <Label className="text-xs">
            {isAr ? "القنوات" : "Channels"}
          </Label>
          <ChannelRow
            label={isAr ? "داخل التطبيق (🔔)" : "In-app (🔔 inbox)"}
            on={p.channels.inApp}
            onChange={v => save({ ...p, channels: { ...p.channels, inApp: v } })}
          />
          <ChannelRow
            label={isAr ? "بريد إلكتروني" : "Email"}
            on={p.channels.email}
            onChange={v => save({ ...p, channels: { ...p.channels, email: v } })}
          />
          <ChannelRow
            label={isAr ? "إشعارات الجوال" : "Mobile push"}
            on={p.channels.push}
            onChange={v => save({ ...p, channels: { ...p.channels, push: v } })}
          />
          <ChannelRow
            label={isAr ? "إشعارات المتصفح (قريباً)" : "Browser push (coming soon)"}
            desc={
              isAr
                ? "يتطلب إعداداً إضافياً؛ البريد يغطي حالياً عند عدم فتح التطبيق."
                : "Needs extra setup; email covers the away-from-app case for now."
            }
            on={false}
            disabled
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs">{isAr ? "الأحداث" : "Events"}</Label>
          {(Object.keys(evLabel) as (keyof NPrefs["events"])[]).map(k => (
            <ChannelRow
              key={k}
              label={evLabel[k]}
              on={p.events[k]}
              onChange={v =>
                save({ ...p, events: { ...p.events, [k]: v } })
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}
